<script lang="ts" setup>
import { sleep } from '@antfu/utils';

const emits = defineEmits(['focus', 'blur', 'submit', 'select-suggestion', 'close']);

type Suggestion = {
  value: string;
  data?: any;
};

const modelValue = defineModel<string>({ default: '' });

const props = defineProps<{
  name: string;
  type?: string;
  isLoading?: boolean;
  isShowClearBtn?: boolean;
  isSuccess?: boolean;
  error?: string;
  hint?: string;
  maskOptions?: any;
  maxLen?: number;
  minLen?: number;
  prependIcon?: string;
  appendButtonIcon?: string;
  placeholder?: string;
  disabled?: boolean;
  autofocus?: boolean;
  autocomplete?: boolean;
  notHideSuggestionsOnClick?: boolean;
  suggestions?: Suggestion[];
  selectedSuggestion?: Suggestion;
}>();

const input = ref();
const isFocused = ref(false);
const isHovered = ref(false);
const isSuggestionsBlockHovered = ref(false);

const errorText = ref('');

watch(
  () => props.error,
  () => (errorText.value = props.error ?? ''),
  { immediate: true },
);

const computedClasses = computed(() => [
  { 'input-field__focus': isFocused.value },
  { 'input-field__hover': isHovered.value },
  { 'input-field__error': errorText.value },
  { 'input-field__success': props.isSuccess },
  { 'input-field__prepend-icon': !!props.prependIcon },
]);
const isShowBottomText = computed(() => !!errorText.value || !!props.hint);
const isShowSuggestionsContainer = computed(() => props.autocomplete && !!props.suggestions?.length && isFocused.value);

const isCounterError = ref(false);

debouncedWatch(
  () => modelValue.value,
  (value) => {
    if (props.minLen) {
      isCounterError.value = value.length !== 0 && value.length < props.minLen;
      errorText.value = isCounterError.value ? `Минимум ${props.minLen} символов` : '';
    }

    if (props.maxLen && !isCounterError.value) {
      isCounterError.value = value.length > props.maxLen;
      errorText.value = isCounterError.value ? 'Превышено количество символов' : '';
    }
  },
  { debounce: 500 },
);

onMounted(() => {
  if (props.autofocus) focus();
});

function submit() {
  if (modelValue.value.length) {
    emits('submit');
    blur();
  }
}

function focus() {
  input.value && input.value.focus();
  isFocused.value = true;
  emits('focus');
}

function blur() {
  input.value && input.value.blur();
  isFocused.value = false;
  emits('blur');
}

async function handleBlur() {
  await sleep(100);

  if (
    isSuggestionsBlockHovered.value &&
    props.notHideSuggestionsOnClick &&
    props.suggestions &&
    props.suggestions.length > 0
  ) {
    focus();
  } else {
    blur();
  }
}

function handleSuggestionClick(suggestion: Suggestion) {
  emits('select-suggestion', suggestion);
}

function handleKeyboardEvents(e: KeyboardEvent) {
  if (e.key === 'Escape') {
    blur();
  }

  if (props.suggestions && props.suggestions.length > 0) {
    if (e.key === 'ArrowUp') {
      console.log('up');
    }
    if (e.key === 'ArrowDown') {
      console.log('down');
    }
  }
}

function clearAndClose() {
  modelValue.value = '';
  blur();
  emits('close');
}
</script>

<template>
  <div>
    <div
      :class="computedClasses"
      class="input-field"
      @keydown="handleKeyboardEvents"
    >
      <component
        :is="`Svgo${prependIcon}`"
        v-if="prependIcon"
        :font-controlled="false"
        class="input-field__icon"
        filled
      />

      <input
        ref="input"
        v-maska="maskOptions"
        :disabled="disabled"
        :name="name"
        :placeholder="placeholder"
        :type="type ?? 'text'"
        :value="modelValue"
        class="input-field__input"
        @blur="handleBlur"
        @focus="focus"
        @input="(e) => (modelValue = (e.target as HTMLInputElement)?.value)"
        @mouseout="isHovered = false"
        @mouseover="isHovered = true"
        @keydown.enter="submit"
      />

      <div
        :class="{ 'input-field__buttons--show-clear-btn': isShowClearBtn }"
        class="input-field__buttons"
      >
        <div :class="{ 'pr-2': !appendButtonIcon }">
          <TransitionGroup
            class="flex gap-2"
            name="loader"
            tag="div"
          >
            <SvgoSpinner
              v-if="isLoading"
              class="animate-spin"
              title="Идет загрузка..."
            />

            <div
              v-if="appendButtonIcon && isLoading"
              class="h-4 w-px bg-black-20"
            />
          </TransitionGroup>
        </div>

        <button
          v-if="appendButtonIcon"
          :disabled="!modelValue.length"
          class="input-field__action-button"
          type="button"
          @click="submit"
        >
          <component
            :is="`Svgo${appendButtonIcon}`"
            :font-controlled="false"
            class="size-5 sm:size-6"
            filled
          />
        </button>

        <div
          v-if="appendButtonIcon && isShowClearBtn"
          class="h-4 w-px bg-black-20"
        />

        <button
          v-if="isShowClearBtn"
          class="input-field__action-button"
          type="button"
          @click="clearAndClose"
        >
          <SvgoCross
            :font-controlled="false"
            class="size-5 sm:size-6"
            filled
          />
        </button>
      </div>

      <FoldableBlock v-model="isShowSuggestionsContainer">
        <ul
          class="input-field__autocomplete-container"
          @mouseleave="isSuggestionsBlockHovered = false"
          @mouseover="isSuggestionsBlockHovered = true"
        >
          <li
            v-for="(suggestion, key) in suggestions"
            :key
            :class="{ 'text-green': suggestion.value === selectedSuggestion?.value }"
            class="input-field__autocomplete-item"
            @click="handleSuggestionClick(suggestion)"
          >
            <span v-html="highlightTextOccurrence(suggestion.value, modelValue)" />
          </li>
        </ul>
      </FoldableBlock>
    </div>

    <FoldableBlock v-model="isShowBottomText">
      <div class="text-xs pt-1.5">
        <span
          v-if="errorText"
          class="text-red"
          v-html="errorText"
        />

        <span
          v-else-if="hint"
          class="text-black-60"
        >
          {{ hint }}
        </span>
      </div>
    </FoldableBlock>
  </div>
</template>

<style lang="scss" scoped>
.input-field {
  position: relative;
  display: flex;
  align-items: center;
  height: 38px;
  border: 1px solid $black-20;
  border-radius: 8px;
  background-color: $black-5;
  transition: all 0.2s;

  &__hover {
    border-color: $black-40;
  }

  &__focus {
    border-color: $black-80;
  }

  &__icon {
    width: 20px;
    height: 20px;
    color: $black-80;
  }

  &__input {
    width: 100%;
    height: 36px;
    padding: 8px;
    border-radius: inherit;
    background-color: $black-5;
    color: $black;

    &::placeholder {
      color: $black-40;
    }

    &:hover,
    &:focus {
      outline: none;
    }

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      margin: 0;
      appearance: none;
    }

    &[type='number'] {
      appearance: textfield;
    }

    &:disabled {
      color: $black-20;
    }
  }

  &__success {
    border-color: $green;
    color: $green;
  }

  &__error {
    border-color: $red;
    color: $red;
  }

  &__prepend-icon {
    padding-left: 8px;
  }

  &__buttons {
    position: absolute;
    right: 6px;
    display: flex;
    gap: 6px;
    align-items: center;
  }

  &__action-button {
    transition: color 0.2s;

    &:hover {
      color: $black;
    }

    &:disabled {
      color: $black-40;
      pointer-events: none;
    }
  }

  &__autocomplete-container {
    position: absolute;
    top: 105%;
    left: -1px;
    z-index: 10;
    display: flex;
    flex-direction: column;
    overflow-y: scroll;
    width: calc(100% + 2px);
    height: fit-content;
    max-height: 200px;
    margin: 0;
    padding: 8px 11px;
    border: 1px solid $black-10;
    border-top: none;
    border-radius: 12px;
    background-color: $white;
    list-style: none;
    line-height: 1.25;
  }

  &__autocomplete-item {
    padding-top: 0.25rem;
    padding-bottom: 0.25rem;
    color: $black-80;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      color: $label-violet;
    }
  }
}
</style>
